/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import Localized from '../Localized';

const StyledSpan = styled.span`
  padding-left: 0.75rem;
  text-transform: uppercase;
  letter-spacing: .0625em;
  color: #706E6B;
`;

const LabelSpan = styled.span`
  padding-left: 0.25rem;
`;

const MenuWrapper = styled.div`
  position: absolute;
  display: inline-block;
  padding: 0 0.75rem;
`;

const visibleMenu = `
  visibility: visible;
  opacity: 1;
  transition: opacity .1s linear,visibility .1s linear;
`;

const triangle = `
  &:before {
    left: auto;
    right: 1rem;

    top: -0.5rem;
    margin-right: -0.5rem;

    box-sizing: border-box;

    width: 1rem;
    height: 1rem;
    position: absolute;
    transform: rotate(45deg);
    content: '';
    background-color: inherit;
    top: -0.5rem;
    margin-left: -0.5rem;
  }

  &:after {
    left: auto;
    right: 1rem;
    top: -0.5rem;
    margin-right: -0.5rem;

    box-shadow: -1px -1px 0 0 rgba(0, 0, 0, 0.16);
    z-index: -1;


    width: 1rem;
    height: 1rem;
    position: absolute;
    transform: rotate(45deg);
    content: '';
    background-color: inherit;
    margin-left: -0.5rem;
  }
`;

const Menu = styled.div`
  visibility: hidden;
  opacity: 0;
  transition: opacity .1s linear,visibility .1s linear;
  top: ${(p) => {
    return (p.top ? p.top : 'calc(100% + 0.5rem)');
  }};
  right: ${(p) => {
    return (p.offsetRight ? p.offsetRight : 'calc(50% - 1.45rem)');
  }};
  position: absolute;
  z-index: 7000;
  float: left;
  min-width: 6rem;
  max-width: 20rem;
  width: ${(p) => {
    return p.style && p.style.right;
  }};
  margin-top: .125rem;
  border: 1px solid #d8dde6;
  border-radius: .25rem;
  padding: .25rem 0;
  font-size: .75rem;
  background: #fff;
  box-shadow: 0 2px 3px 0 rgba(0,0,0,.16);

  ${visibleMenu}
  ${(p) => {
    return !p.hideTriangle && triangle;
  }}
`;

const Anchor = styled.a`
  line-height: 18px;
  background-color: ${(props) => {
    return props.bgColor || 'transparent';
  }};
  transition: color .1s linear;
  position: relative;
  display: flex;
  justify-content: space-between;
  padding: .5rem .75rem;
  color: #16325c;
  white-space: nowrap;
  cursor: pointer;
  align-items: center;
  :hover {
    outline: 0;
    text-decoration: none;
    background-color: #f4f6f9;
  };
`;

const OptionSpan = styled.span`
  > span:first-child {
    margin-right: 0.5rem;
  }
`;

const ScreenReaderOnly = styled.span`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

export default class IconDropdownMenu extends React.Component {
  static propTypes = {
    icon: PropTypes.object,
    menuOptions: PropTypes.array,
  }

  render() {
    const {
      icon, style, heading, menuOptions, offsetRight,
      skipSeparator, hideTriangle, top, openOnSelect
    } = this.props;

    return (
      <MenuWrapper>
        {icon}
        <Menu top={top} offsetRight={offsetRight} style={style} hideTriangle={hideTriangle}>
          <ul role='menu'>
            {!skipSeparator &&
              <li title={heading} role='separator' aria-hidden='true'>
                <StyledSpan> <Localized modelKey={heading}
                  modelAttribute='heading' modelName='iconDropdownMenu' /> </StyledSpan>
              </li>
            }
            {menuOptions.map((option, i) => {
              const titleSpan = <OptionSpan>
                {option.icon}
                <LabelSpan>{option.label}</LabelSpan>
              </OptionSpan>;

              return (<li key={i} className={option.classname}
                onClick={(event) => {
                  option.onClick(event);
                  if (openOnSelect) openOnSelect();
                }}
                role='presentation'>
                {option.readOnly ? titleSpan : <Anchor
                  href={option.url}
                  bgColor={option.bgColor}
                  role='menuitem'
                  className={option.padding}
                >
                  <ScreenReaderOnly>{option.title}</ScreenReaderOnly>
                  {titleSpan}
                </Anchor>
                }
              </li>
              );
            })}
          </ul>
        </Menu>
      </MenuWrapper>
    );
  }
}
